/**
 * WhatsApp feature API client.
 * Uses the centralized talkvitApi instance from src/api/talkvitAPI.
 *
 * @deprecated Import `talkvitApi` directly from "src/api/talkvitAPI" for new code.
 * This file re-exports for backward compatibility.
 */
import { talkvitApi } from "../../../api/talkvitAPI";
import { WHATSAPP_ENDPOINTS } from "./client.endpoints";
import {
  IWhatsAppContact,
  IWhatsAppContactMetadata,
  IActionEvent,
  IWhatsappMsgMediaHistory,
} from "../context/WhatsAppChatProvider/WhatsApp.types";

/**
 * @deprecated Use `talkvitApi` from "src/api/talkvitAPI" instead.
 */
export const whatsAppHttpClient = talkvitApi;

// ─── Response envelope (matches backend standard) ────────────────

interface IApiResponse<T> {
  message: string;
  totalRecords?: number;
  currentRecords?: number;
  data: T;
  meta?: any;
}

// ─── Contacts ────────────────────────────────────────────────────

export interface IFetchContactsParams {
  companyOutletUuid: string;
  whatsappWebService: boolean;
  pageNumber?: number | null;
  rowsInPerPage?: number | null;
  date?: { fromDate: string; toDate: string };
  recipientNumber?: string;
  filters?: string[];
}

export const fetchContactsApi = async (params: IFetchContactsParams) => {
  const { companyOutletUuid, whatsappWebService, pageNumber, rowsInPerPage, date, recipientNumber, filters } = params;

  let url = `${WHATSAPP_ENDPOINTS.GET_CONTACTS}?company_outlet_uuid=${companyOutletUuid}&whatsapp_web_service=${whatsappWebService}`;
  if (pageNumber) url += `&pageNo=${pageNumber}`;
  if (rowsInPerPage) url += `&itemPerPage=${rowsInPerPage}`;
  if (date?.fromDate) url += `&from_date=${date.fromDate}`;
  if (date?.toDate) url += `&to_date=${date.toDate}`;
  if (recipientNumber) url += `&columns=recipient_number,recipient_name&value=${recipientNumber}`;
  if (filters?.length) {
    filters.forEach((f) => { url += `&whatsapp_filters=${encodeURIComponent(f)}`; });
  }

  const res = await talkvitApi.get<IApiResponse<IWhatsAppContact[]>>(url);
  return {
    data: res.data.data || [],
    totalRecords: res.data.totalRecords || (res.data.data || []).length,
    message: res.data.message || "",
    meta: (res.data.meta || {}) as IWhatsAppContactMetadata,
  };
};

// ─── Action Events (Sentiment) ──────────────────────────────────

export const fetchActionEventsApi = async (conversationId: string, page = 1, perPage = 10) => {
  const url = `${WHATSAPP_ENDPOINTS.GET_CONVERSATION_SENTIMENT}?pageNo=${page}&itemPerPage=${perPage}&mobile_no=${conversationId}`;
  const res = await talkvitApi.get<IApiResponse<IActionEvent[]>>(url);
  return {
    data: res.data.data || [],
    totalRecords: res.data.totalRecords || 0,
  };
};

// ─── Media History ───────────────────────────────────────────────

export const fetchMediaHistoryApi = async (
  phoneNumber: string,
  companyOutletUuid: string,
  whatsappWebService: boolean,
  page = 1,
  perPage = 10,
) => {
  const url = `${WHATSAPP_ENDPOINTS.GET_MEDIA_HISTORY}?pageNo=${page}&itemPerPage=${perPage}&phone_number=${phoneNumber}&company_outlet_uuid=${companyOutletUuid}&whatsapp_web_service=${whatsappWebService}`;
  const res = await talkvitApi.get<IApiResponse<IWhatsappMsgMediaHistory[]>>(url);
  return {
    data: res.data.data || [],
    currentRecords: res.data.currentRecords || 0,
  };
};

// ─── Module Settings ─────────────────────────────────────────────

export const fetchModuleSettingsApi = async (companyOutletUuid: string, moduleName: string) => {
  const url = `${WHATSAPP_ENDPOINTS.GET_MODULE_SETTINGS}?company_outlet_uuid=${companyOutletUuid}&module_name=${moduleName}`;
  const res = await talkvitApi.get(url);
  return { data: res.data.data || [], totalRecords: res.data.totalRecords || 0 };
};

// ─── User Data Scope (Assignees) ─────────────────────────────────

export const fetchContactAssigneesApi = async (
  companyOutletUuid: string,
  columnName: string,
  columnValue: string,
  moduleName: string,
) => {
  const url = `${WHATSAPP_ENDPOINTS.GET_USER_DATA_SCOPE}?company_outlet_uuid=${companyOutletUuid}&module_name=${moduleName}&table_identifier_column_value=${encodeURIComponent(columnValue)}&table_identifier_column_name=${columnName}&status=ACTIVE`;
  const res = await talkvitApi.get(url);
  return { data: res.data.data || [], totalRecords: res.data.totalRecords || 0 };
};
